import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getRouteProjectDetails, getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { useHistoricalNavigate } from "@/zapi-frontend/shared/hooks/useHistoricalNavigate";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { MemoIsland } from "./MemoIsland/MemoIsland";
import { SidebarIsland } from "./SidebarIsland/SidebarIsland";
import { WorkspaceRoomsIsland } from "./Repository/WorkspaceRoomsIsland";
import { AnalysisIsland } from "./AnalysisIsland";
import { ProjectHeader } from "./ProjectHeader/ProjectHeader";
import { InnoveraLoading, Typography } from "@libs/zapi-ui/custom";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { InstructionsIsland } from "./InstructionsIsland";
import { InstructionsHeader } from "./InstructionsHeader";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { toast } from "sonner";
import { useProjectSetupPolling } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";
import { useMarkChapterNotificationAsRead } from "@/zapi-frontend/features/Notifications";
import { SnippetsStatusList } from "./SnippetsStatusList/SnippetsStatusList";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useBackRedirectFromProjectList } from "@/zapi-frontend/shared/hooks/useBackToProjectOverride";
import { SS_BACK_TO_PROJECT_PRESENTATION } from "@/zapi-frontend/shared/session-storage/keys";
import { DashboardIsland } from "./DashboardIsland/DashboardIsland";
import { ProjectOverviewIsland } from "./ProjectOverviewIsland";
import { GenerationNotificationModal } from "@/zapi-frontend/widgets/GenerationNotificationModal";
import { PresentationPage } from "./PresentationPage/ui/PresentationPage";

export function ProjectDetailsPage() {
  const { id } = useParams();
  const projectId = useMemo(() => (typeof id === "string" ? id : ""), [id]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchSelectedProject, isFetchProjectLoading, setSelectedProject } = useProjectStore();
  const { initializeProjectChapters, viewMode, setViewMode, activeChapter, projectChapters } = useChapterStore();
  const { navigate } = useHistoricalNavigate();
  const { closeHandler, openSidebar } = useProjectSidebarStore();
  const { isLabs } = useMeStore();
  useMarkChapterNotificationAsRead();
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [isProjectLoaded, setIsProjectLoaded] = useState(false);

  const { isPolling, isPollingInitialized, progressMessage, chapterMemoPolling, chapterStatusMap } =
    useProjectSetupPolling(
      projectId,
      activeChapter?.id ?? null,
      isProjectLoaded,
      useCallback(() => {
        fetchSelectedProject(projectId, true);
        setIsProjectLoaded(true);
      }, [projectId, fetchSelectedProject])
    );

  // Extract snippets for active chapter
  const activeChapterSnippets = activeChapter?.id
    ? chapterStatusMap?.[activeChapter.id.toString()]?.snippetsStatus ?? []
    : [];

  // Check if at least one snippet is not pending
  const hasNonPendingSnippet = activeChapterSnippets.some((snippet) => snippet.status !== "pending");
  const chapterMemoStatus = chapterMemoPolling.chapters.find((i) => i.id === activeChapter?.id);
  const isActiveChapterHasActiveMemo = chapterMemoStatus?.hasActiveMemo ?? !!activeChapter?.activeMemo;

  const loadProject = useCallback(async () => {
    const [response, error] = await tryCatch(fetchSelectedProject(projectId));

    if (error) {
      toast.error("Failed to load project");
      navigate(getRouteProjects());
      return;
    }

    if (response) {
      initializeProjectChapters(response);
      setIsProjectLoaded(true);
    }
  }, [projectId, fetchSelectedProject, initializeProjectChapters, navigate]);

  useEffect(() => {
    if (!projectId) return;
    setIsProjectLoaded(false);
    loadProject();
  }, [projectId, loadProject]);

  useEffect(() => {
    const shouldShowModal = searchParams.get("showGenerationModal") === "true";
    if (shouldShowModal) {
      setShowGenerationModal(true);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete("showGenerationModal");
          return params;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const hasChapterId = searchParams.has("chapterId");
    const viewParam = searchParams.get("view");

    if (viewParam === "dashboard" && viewMode !== "dashboard") {
      setViewMode("dashboard");
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete("view");
          return params;
        },
        { replace: true }
      );
    } else if (viewParam === "instructions") {
      setViewMode("instructions");
    } else if (hasChapterId) {
      if (viewMode === "instructions") {
        setViewMode("chapter");
      } else if (viewMode !== "chapter") {
        setViewMode("chapter");
      }
    } else if (projectId && isProjectLoaded && isPollingInitialized) {
      if (viewMode === "instructions") {
        setViewMode(isPolling ? "dashboard" : "projectOverview");
      } else if (!viewParam) {
        setViewMode(isPolling ? "dashboard" : "projectOverview");
      }
    }
  }, [
    searchParams,
    viewMode,
    setViewMode,
    setSearchParams,
    projectId,
    isProjectLoaded,
    projectChapters,
    isPolling,
    isPollingInitialized
  ]);

  useEffect(() => {
    return () => {
      setViewMode("chapter");
      setSelectedProject(null);
      closeHandler();
    };
  }, [setViewMode, setSelectedProject, closeHandler]);

  useEffect(() => {
    if (isFetchProjectLoading || !isActiveChapterHasActiveMemo) closeHandler();
  }, [isFetchProjectLoading, isActiveChapterHasActiveMemo, closeHandler]);

  const isDisabledDueToProjectSetup = isPolling && !isActiveChapterHasActiveMemo;
  const isViewModeResolved = searchParams.has("chapterId") || isPolling !== undefined;

  const isPresentationTab = searchParams.get("tab") === "presentation";

  useBackRedirectFromProjectList({
    projectId,
    isActive: isPresentationTab,
    storageKey: SS_BACK_TO_PROJECT_PRESENTATION,
    returnUrl: `${getRouteProjectDetails(projectId)}?tab=document`
  });

  if (isPresentationTab) return <PresentationPage />;

  return (
    <div
      className={cn(
        "flex bg-innoveraZinc-100 gap-2 h-[calc(100svh-56px)] w-full p-2 overflow-hidden hd:pl-[70px] relative",
        isFetchProjectLoading && "pl-2 hd:pl-2"
      )}
    >
      <SidebarIsland chapterMemoPolling={chapterMemoPolling} />
      <div className="flex flex-col w-full h-full bg-white">
        {viewMode === "instructions" && <InstructionsHeader />}
        {viewMode !== "instructions" && <ProjectHeader chapterMemoPolling={chapterMemoPolling} />}
        <div
          className={cn(
            "grid w-full grid-cols-[1fr_0px] h-[calc(100svh-128px)] transition-all duration-300",
            activeChapter?.projectTemplateChapter.isExecutiveSummary && "max-h-[calc(100svh-270px)]",
            !!openSidebar && "grid-cols-[0px_1fr] tablet:grid-cols-[1fr_312px]"
          )}
        >
          {(isFetchProjectLoading || isDisabledDueToProjectSetup || !isViewModeResolved) &&
            viewMode !== "dashboard" &&
            viewMode !== "projectOverview" && (
              <div className="w-full pt-12 flex flex-col justify-center items-center col-span-2 gap-4 ">
                <InnoveraLoading size={90} />
                <Typography variant="body2">
                  {isDisabledDueToProjectSetup ? progressMessage : "Loading project..."}
                </Typography>
                {isDisabledDueToProjectSetup && isLabs && activeChapterSnippets.length > 0 && hasNonPendingSnippet && (
                  <SnippetsStatusList snippets={activeChapterSnippets} />
                )}
              </div>
            )}
          {viewMode === "dashboard" && (
            <DashboardIsland chapterStatusMap={chapterStatusMap} chapterMemoPolling={chapterMemoPolling} />
          )}
          {viewMode === "projectOverview" && <ProjectOverviewIsland />}
          {!isFetchProjectLoading &&
            !isDisabledDueToProjectSetup &&
            isViewModeResolved &&
            viewMode !== "dashboard" &&
            viewMode !== "projectOverview" && (
              <div className="w-full flex">
                {viewMode === "instructions" && <InstructionsIsland />}
                {viewMode !== "instructions" && (
                  <div className="w-full laptop:px-7">
                    <MemoIsland />
                  </div>
                )}
              </div>
            )}
          <AnalysisIsland projectId={projectId} isChapterContentReady={isActiveChapterHasActiveMemo} />
        </div>
      </div>
      {viewMode !== "dashboard" &&
        viewMode !== "projectOverview" &&
        viewMode !== "instructions" &&
        isProjectLoaded && (
          <WorkspaceRoomsIsland chapterName={activeChapter?.projectTemplateChapter.name} />
        )}
      <GenerationNotificationModal open={showGenerationModal} onClose={() => setShowGenerationModal(false)} />
    </div>
  );
}
